import type { AncestralDef, AncestralId } from '../types';

/**
 * The Ancestral Memory tree. Bought with Ancestral Knowledge, never reset.
 * Effects scale linearly with purchased level; costs scale geometrically.
 */
export const ANCESTRAL_NODES: AncestralDef[] = [
  // --- Production ----------------------------------------------------------
  {
    id: 'deepRoots',
    name: 'Deep Roots',
    glyph: 'leaf',
    description: 'The tribe remembers which ground gives. Forage +15% per level.',
    minAge: 0,
    maxLevel: 25,
    baseCost: 2,
    costGrowth: 1.55,
    effects: [{ kind: 'resourceMult', resource: 'forage', amount: 0.15 }],
  },
  {
    id: 'keenEye',
    name: 'Keen Eye',
    glyph: 'stone',
    description: 'Good stone is recognised on sight. Flint +15% per level.',
    minAge: 0,
    maxLevel: 25,
    baseCost: 2,
    costGrowth: 1.55,
    effects: [{ kind: 'resourceMult', resource: 'flint', amount: 0.15 }],
  },
  {
    id: 'dryHands',
    name: 'Dry Hands',
    glyph: 'grass',
    description: 'Tinder is kept the way it was always kept. Tinder +15% per level.',
    minAge: 0,
    maxLevel: 25,
    baseCost: 2,
    costGrowth: 1.55,
    effects: [{ kind: 'resourceMult', resource: 'tinder', amount: 0.15 }],
  },
  {
    id: 'emberBlood',
    name: 'Ember in the Blood',
    glyph: 'flame',
    description: 'Everything the tribe does, it does a little better. All production +6% per level.',
    minAge: 0,
    maxLevel: 30,
    baseCost: 10,
    costGrowth: 1.7,
    requires: ['deepRoots', 'keenEye'],
    effects: [{ kind: 'globalMult', amount: 0.06 }],
  },
  {
    id: 'oldWays',
    name: 'The Old Ways',
    glyph: 'eye',
    description: 'Elders teach faster than they did. Insight +20% per level.',
    minAge: 0,
    maxLevel: 20,
    baseCost: 6,
    costGrowth: 1.6,
    effects: [{ kind: 'resourceMult', resource: 'insight', amount: 0.2 }],
  },

  // --- Population ----------------------------------------------------------
  {
    id: 'manyHands',
    name: 'Many Hands',
    glyph: 'tribe',
    description: 'The line begins larger. Start each generation with +1 tribe member per level.',
    minAge: 0,
    maxLevel: 25,
    baseCost: 4,
    costGrowth: 1.75,
    effects: [{ kind: 'startPopulation', amount: 1 }],
  },
  {
    id: 'fullBellies',
    name: 'Full Bellies',
    glyph: 'leaf',
    description: 'Nothing is wasted. Food eaten per head −5% per level.',
    minAge: 0,
    maxLevel: 12,
    baseCost: 8,
    costGrowth: 1.65,
    effects: [{ kind: 'consumptionMult', amount: -0.05 }],
  },
  {
    id: 'fertileGround',
    name: 'Fertile Ground',
    glyph: 'grain',
    description: 'The tribe grows toward its shelter faster. Population growth +20% per level.',
    minAge: 1,
    maxLevel: 15,
    baseCost: 7,
    costGrowth: 1.6,
    effects: [{ kind: 'growthMult', amount: 0.2 }],
  },
  {
    id: 'openHearth',
    name: 'Open Hearth',
    glyph: 'home',
    description: 'Room is always found for one more. Shelter capacity +3 per level.',
    minAge: 1,
    maxLevel: 20,
    baseCost: 9,
    costGrowth: 1.6,
    effects: [{ kind: 'shelter', amount: 3 }],
  },

  // --- Head starts ---------------------------------------------------------
  {
    id: 'firstSpark',
    name: 'First Spark',
    glyph: 'ember',
    description: 'The line does not begin empty. Start with +60 forage, flint and tinder per level.',
    minAge: 0,
    maxLevel: 15,
    baseCost: 3,
    costGrowth: 1.5,
    effects: [
      { kind: 'startResource', resource: 'forage', amount: 60 },
      { kind: 'startResource', resource: 'flint', amount: 60 },
      { kind: 'startResource', resource: 'tinder', amount: 60 },
    ],
  },
  {
    id: 'keptCoals',
    name: 'Kept Coals',
    glyph: 'flame',
    description: 'Controlled Fire is known from the first day of every generation.',
    minAge: 1,
    maxLevel: 1,
    baseCost: 30,
    costGrowth: 1,
    effects: [{ kind: 'startTech', tech: 'controlledFire' }],
  },
  {
    id: 'boundBlade',
    name: 'Bound Blade',
    glyph: 'spear',
    description: 'Hafting is known from the first day of every generation.',
    minAge: 2,
    maxLevel: 1,
    baseCost: 120,
    costGrowth: 1,
    requires: ['keptCoals'],
    effects: [{ kind: 'startTech', tech: 'hafting' }],
  },
  {
    id: 'twistedRope',
    name: 'Twisted Rope',
    glyph: 'thread',
    description: 'Cordage is known from the first day of every generation.',
    minAge: 3,
    maxLevel: 1,
    baseCost: 500,
    costGrowth: 1,
    requires: ['boundBlade'],
    effects: [{ kind: 'startTech', tech: 'cordage' }],
  },
  {
    id: 'markedWall',
    name: 'The Marked Wall',
    glyph: 'hand',
    description: 'Symbols are known from the first day of every generation.',
    minAge: 4,
    maxLevel: 1,
    baseCost: 2500,
    costGrowth: 1,
    requires: ['twistedRope'],
    effects: [{ kind: 'startTech', tech: 'symbols' }],
  },
  {
    id: 'savedSeed',
    name: 'The Saved Seed',
    glyph: 'grain',
    description: 'The Seed is known from the first day of every generation.',
    minAge: 5,
    maxLevel: 1,
    baseCost: 12000,
    costGrowth: 1,
    requires: ['markedWall'],
    effects: [{ kind: 'startTech', tech: 'theSeed' }],
  },

  // --- Meta ----------------------------------------------------------------
  {
    id: 'ancestorsFavour',
    name: 'The Ancestors’ Favour',
    glyph: 'skull',
    description: 'Ancestral Knowledge gained on passing the fire +12% per level.',
    minAge: 1,
    maxLevel: 30,
    baseCost: 15,
    costGrowth: 1.7,
    effects: [{ kind: 'akGainMult', amount: 0.12 }],
  },
  {
    id: 'wideStores',
    name: 'Wide Stores',
    glyph: 'pot',
    description: 'Every store holds more. All storage +25% per level.',
    minAge: 1,
    maxLevel: 20,
    baseCost: 6,
    costGrowth: 1.55,
    effects: [{ kind: 'storageMult', resource: '*', amount: 0.25 }],
  },
  {
    id: 'oldTales',
    name: 'Old Tales',
    glyph: 'spiral',
    description: 'Fortune leans toward the tribe. Favourable tales +20% more likely per level.',
    minAge: 1,
    maxLevel: 10,
    baseCost: 12,
    costGrowth: 1.6,
    effects: [{ kind: 'taleLuck', amount: 0.2 }],
  },

  // --- Active play and away time ------------------------------------------
  {
    id: 'swiftHands',
    name: 'Swift Hands',
    glyph: 'hand',
    description: 'Working alongside the tribe counts for more. Vigor +30% stronger per level.',
    minAge: 0,
    maxLevel: 10,
    baseCost: 5,
    costGrowth: 1.6,
    effects: [{ kind: 'vigorMult', amount: 0.3 }],
  },
  {
    id: 'steadyBreath',
    name: 'Steady Breath',
    glyph: 'spiral',
    description: 'The tribe keeps pace longer. Maximum Vigor +5 stacks per level.',
    minAge: 1,
    maxLevel: 8,
    baseCost: 14,
    costGrowth: 1.65,
    requires: ['swiftHands'],
    effects: [{ kind: 'vigorStacks', amount: 5 }],
  },
  {
    id: 'longSleep',
    name: 'The Long Sleep',
    glyph: 'moon',
    description: 'The tribe carries on without you for longer. Offline progress +8 hours per level.',
    minAge: 0,
    maxLevel: 6,
    baseCost: 8,
    costGrowth: 1.8,
    effects: [{ kind: 'offlineHours', amount: 8 }],
  },
];

export const ANCESTRAL_BY_ID: Record<AncestralId, AncestralDef> = Object.fromEntries(
  ANCESTRAL_NODES.map((node) => [node.id, node]),
);

export function getAncestralNode(id: AncestralId): AncestralDef | undefined {
  return ANCESTRAL_BY_ID[id];
}
